<template>
  <b-card :title="$t(chartData.title)">
    <b-row>
      <b-col
        :id="alias"
        :lg="typeOfChart"
        md="12"
        sm="12"
        class="chart"
        :style="{ minHeight: height }"
      />
      <b-col :lg="typeOfChart" md="12" sm="12" class="table">
        <b-table
          :items="data"
          :fields="fields"
          :striped="percentageSelected === 'response'"
          outlined
        >
          <template v-for="field in fields" #head(field.key)="">
            {{ $t(labels[field.key]) }}
          </template>

          <template #cell(convention)="row">
            <div
              v-if="!row.item.label.includes('_option_')"
              class="convention"
              :style="{
                backgroundColor: $t(changeLabel(row.item.label) + '_color'),
              }"
            />
            <div
              v-if="row.item.label.includes('_option_')"
              class="convention"
              :style="{
                backgroundColor: $t(
                  'label_reports_graph_response' + (row.index + 1) + '_color'
                ),
              }"
            />
          </template>
          <template #cell(label)="row">
            {{ $t(changeLabel(row.item.label)) }}
          </template>
          <template #cell(value)="row">
            {{ row.item.value
            }}{{
              row.item.percentage !== undefined
                ? ` (${(row.item.percentage * 100).toFixed(2)}%)`
                : ""
            }}
          </template>
        </b-table>
        <div
          v-if="type === 'responses'"
          class="card-chart-survey-percentage-selector"
        >
          <div>
            {{ $t("label_opinionSurvey_report_toogle_showPercentageBy") }}
          </div>
          <div
            :class="
              percentageSelected !== 'response'
                ? 'card-chart-survey-percentage-selector-selectable'
                : 'card-chart-survey-percentage-selector-selected'
            "
            @click="changePercentageView('response')"
          >
            {{ $t("label_opinionSurvey_report_toogle_responses") }}
          </div>
          <div
            :class="
              percentageSelected === 'response'
                ? 'card-chart-survey-percentage-selector-selectable'
                : 'card-chart-survey-percentage-selector-selected'
            "
            @click="changePercentageView('demographic')"
          >
            {{ $t("label_opinionSurvey_report_toogle_demographic") }}
          </div>
        </div>
        <template #head(convention)="" />
      </b-col>
    </b-row>
  </b-card>
</template>

<script>
import { ChartPie } from "@/utils/ChartPie";
import { ChartBarStackedHorizontal } from "@/utils/ChartBarStackedHorizontal";

export default {
  name: "CardChartSurvey",
  props: {
    alias: {
      type: String,
      default: "",
    },
    height: {
      type: String,
      default: "auto",
    },
    section: {
      type: String,
      default: "",
    },
    question: {
      type: String,
      default: "",
    },
    filterCriteria: {
      type: String,
      default: "",
    },
    type: {
      type: String,
      default: "participation",
    },
    typeOfChart: {
      type: Number,
      default: 6,
    },
    survey: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      chartData: {},
      fields: [],
      data: [],
      percentageSelected: "response",
    };
  },
  watch: {
    alias: function (newVal, oldVal) {
      var svg = this.$d3.select("#" + oldVal);
      svg.selectAll(".chart").remove();
      setTimeout(() => {
        this.updateData();
      }, 200);
    },
  },
  mounted() {
    this.updateData();
  },
  methods: {
    changePercentageView(view) {
      this.percentageSelected = view;
      this.updateTable();
    },
    changeLabel(label) {
      switch (label) {
        default:
          return label;
      }
    },
    getOpinionSurveyQuestionData(section, question, filterCriteria) {
      const s = this.survey.sections.find((x) => x.name === section);
      const q = s.questions.find((x) => x.name === question);
      const fc = q.filterCriteria.find((x) => x.name === filterCriteria);
      return fc;
    },
    updateData() {
      this.questionData = this.getOpinionSurveyQuestionData(
        this.section,
        this.question,
        this.filterCriteria
      );
      if (this.type === "participation") {
        // Calculate the percentage
        const chartData = this.questionData.participationChart.data;
        const dataValues = {};
        for (const question of chartData) {
          dataValues[question.label] = question.value;
        }
        chartData.forEach((item) => {
          // Calculates the total of the elements
          const total = this.getTotal(dataValues);
          if (total !== 0) {
            item.type = "integer";
            item.percentage = (100 * item.value) / total / 100;
          }
        });
        this.chartData = this.questionData.participationChart;
      } else {
        // Calculate the percentage
        const chartData = this.questionData.responsesChart.data;
        const dataValues = {};
        for (const question of chartData) {
          for (const group of question.group) {
            dataValues[group.label] = dataValues[group.label] || {};
            dataValues[group.label][question.label] = group.value;
          }
        }
        chartData.forEach((labels) => {
          labels.group.forEach((groups) => {
            // Calculates the total of the elements
            const total = this.getTotal(dataValues[groups.label]);
            if (total !== 0) {
              groups.percentage = ((100 * groups.value) / total).toFixed(2);
            }
            const totalDemographic = this.getDemographicTotal(
              dataValues,
              labels.label
            );
            if (totalDemographic !== 0) {
              groups.percentageDemographic = (
                (100 * groups.value) /
                totalDemographic
              ).toFixed(2);
            }
          });
        });
        const yAxis = this.questionData.responsesChart.xAxis;
        const xAxis = this.questionData.responsesChart.yAxis;
        this.questionData.responsesChart.xAxis = xAxis;
        this.questionData.responsesChart.yAxis = yAxis;
        this.chartData = this.questionData.responsesChart;
      }
      this.updateChart();
      this.updateTable();
    },
    getTotal(entries) {
      return Object.entries(entries).reduce((accumulator, currentData) => {
        const [, value] = currentData;
        return accumulator + value;
      }, 0);
    },
    getDemographicTotal(data, demographic) {
      let total = 0;
      Object.entries(data).map((currentData) => {
        const [, value] = currentData;
        const numericValue = Number(value[demographic]);
        if (!isNaN(numericValue)) {
          total += numericValue;
        }
      });
      return total;
    },
    updateChart() {
      if (this.type === "participation") {
        this.chart = new ChartPie(this.alias, this.chartData, 80);
      } else {
        this.chart = new ChartBarStackedHorizontal(
          this.alias,
          this.chartData,
          true,
          true
        );
      }
      this.chart.render();
    },
    updateTable() {
      if (this.type === "participation") {
        this.labels = {
          convention: "",
          label: this.chartData.xAxis,
          value: this.chartData.yAxis,
        };
        this.fields = [
          { key: "convention" },
          { key: "label" },
          { key: "value" },
        ];
        this.data = this.chartData.data;
      } else {
        this.labels = {
          convention: "",
          label: this.chartData.yAxis,
        };
        this.fields = [{ key: "convention" }, { key: "label" }];
        this.chartData.data.map((i) => {
          this.fields.push({ key: i.label });
          this.labels[i.label] = i.label;
        });

        // Agregamos los labels necesarios
        var labels = [];
        this.chartData.data[0].group.map((i) => {
          labels.push(i.label);
        });

        this.data = [];

        labels.map((i) => {
          var obj = {
            label: i,
          };
          this.chartData.data.map((j) => {
            j.group.map((k) => {
              if (i === k.label) {
                if (this.percentageSelected === "response") {
                  obj[j.label] = `${k.value} ${
                    k.percentage !== undefined && k.percentage > 0
                      ? ` (${k.percentage}%)`
                      : ""
                  }`;
                } else {
                  obj[j.label] = `${k.value} ${
                    k.percentageDemographic !== undefined &&
                    k.percentageDemographic > 0
                      ? ` (${k.percentageDemographic}%)`
                      : ""
                  }`;
                }
              }
            });
          });
          this.data.push(obj);
        });
      }
      this.fields = Object.keys(this.labels).map((s, index) => {
        return {
          key: s,
          label: this.$t(this.labels[s]),
          class:
            index % 2 === 0 &&
            index > 1 &&
            this.percentageSelected !== "response"
              ? "column-percentage"
              : "",
        };
      });
    },
  },
};
</script>
<style lang="scss" scoped>
@import "../../assets/css/variables.scss";

.card-chart-survey {
  &-percentage-selector {
    display: flex;
    justify-content: center;

    div {
      border: 1px solid $table-border-color;
      margin-left: -1px;
      padding: 10px 20px;
      font-weight: bold;
    }

    &-selectable {
      color: $primary-color;
      font-weight: normal;
      cursor: pointer;
    }

    &-selected {
      background-color: $primary-color;
      color: white;
      font-weight: normal;
    }
  }
}

.card-body {
  flex: none;
  font-family: $font-subtitle;
  height: 100%;
}

.card-title {
  text-align: center;
}

.b-table {
  font-family: $font-text;
  font-size: 0.8rem;
  margin-top: 1rem;
}

.convention {
  background-color: #000;
  width: 1.5rem;
  height: 1.5rem;
  margin: 0;
}

</style>

<style lang="scss">
.column-percentage {
  background-color: #d1d3d4;
}
</style>

